import { TuiDay, TuiTime } from '@taiga-ui/cdk';
import { inject } from '@angular/core';
import { AbstractControl, Validators } from '@angular/forms';
import { MaxInputLength } from '@context/shared/types/form';
import { TranslocoService } from '@jsverse/transloco';
import { AcceptedFiles, AcceptedImages } from '@context/shared/types/media';

const PasswordPropertyValidators = [
	'length',
	'special',
	'number',
	'lower',
	'upper',
] as const;
type PasswordPropertyValidator = (typeof PasswordPropertyValidators)[number];

type PasswordValidators<T = string> = {
	[key in PasswordPropertyValidator]: T;
};

const RegExpValidators: PasswordValidators<RegExp> = {
	length: new RegExp(/^.{8,20}$/),
	special: new RegExp('[!-\\/:-@[-`{-~]'),
	number: new RegExp('[0-9]'),
	lower: new RegExp('[a-z]'),
	upper: new RegExp('[A-Z]'),
};

export function applyValidators<T extends new (...args: any[]) => any>(
	Base: T,
) {
	return class extends Base {
		readonly maxLength = MaxInputLength;

		readonly acceptedFiles = AcceptedFiles.join(',');
		readonly acceptedImages = AcceptedImages.join(',');

		readonly transloco = inject(TranslocoService);

		getRequiredValidator = (field: AbstractControl, translation: string) =>
			Validators.required(field)
				? { required: this.transloco.translate(translation) }
				: null;

		getEmailValidator = (field: AbstractControl, translation: string) =>
			Validators.email(field)
				? { email: this.transloco.translate(translation) }
				: null;

		getPasswordValidator = (field: AbstractControl) => {
			const length = RegExpValidators.length.test(field.value);
			const special = RegExpValidators.special.test(field.value);
			const lower = RegExpValidators.lower.test(field.value);
			const upper = RegExpValidators.upper.test(field.value);
			const number = RegExpValidators.number.test(field.value);

			const error: Partial<PasswordValidators> = {};
			if (!length)
				error['length'] = this.transloco.translate(
					'auth.password-validator.length',
				);

			if (!lower)
				error['lower'] = this.transloco.translate(
					'auth.password-validator.lower',
				);

			if (!upper)
				error['upper'] = this.transloco.translate(
					'auth.password-validator.upper',
				);

			if (!number)
				error['number'] = this.transloco.translate(
					'auth.password-validator.number',
				);

			if (!special)
				error['special'] = this.transloco.translate(
					'auth.password-validator.special',
				);

			return Object.keys(error).length ? error : null;
		};

		getMatchingValidator = (
			field: AbstractControl,
			match: AbstractControl,
			translation: string,
		) =>
			field.value !== match.value
				? { match: this.transloco.translate(translation) }
				: null;

		getMaxLengthValidator = (field: AbstractControl, maxLength: number) =>
			field.value.length > maxLength
				? {
						maxLength: this.transloco.translate(
							'form.error.length.max-#',
							{ length: maxLength },
						),
					}
				: null;

		getMinLengthValidator = (field: AbstractControl, minLength: number) =>
			field.value.length < minLength
				? {
						minLength: this.transloco.translate(
							'form.error.length.min-#',
							{ length: minLength },
						),
					}
				: null;

		getPatternValidator = (
			field: AbstractControl,
			pattern: RegExp,
			translation: string | { key: string; params: Record<string, any> },
		) => {
			const isString = typeof translation === 'string';
			const translationKey = isString ? translation : translation.key;
			const translationParams = isString ? {} : translation.params;
			return pattern.test(field.value)
				? null
				: {
						pattern: this.transloco.translate(
							translationKey,
							translationParams,
						),
					};
		};

		getUniqueValidator = (
			field: AbstractControl,
			controls: AbstractControl[],
			translation: string,
		) => {
			if (!controls.length) return null;

			const currentValue = field.value?.trim().toLowerCase();
			if (!currentValue) return null;

			const count = controls.filter(
				(control) =>
					control.value?.trim().toLowerCase() === currentValue,
			).length;

			// Value is 1 because it counts itself
			return count > 1
				? { unique: this.transloco.translate(translation) }
				: null;
		};

		getFutureDateValidator = (
			field: AbstractControl,
			translation: string,
		) => {
			const value = field.value as [TuiDay, TuiTime] | null;
			if (!value) return null;

			const [date, time] = value;
			const selectedDate = date.toLocalNativeDate();

			if (time) {
				selectedDate.setHours(time.hours, time.minutes, 0, 0);
			} else {
				selectedDate.setHours(0, 0, 0, 0);
			}

			const now = new Date();
			// Remove seconds and milliseconds for comparison
			now.setSeconds(0, 0);

			if (selectedDate <= now) return null;
			return { futureDate: this.transloco.translate(translation) };
		};
	};
}
